import React, {FormEvent, RefObject} from 'react';
import {FeedPost} from '../../../../services/socialFeedApi';
import {getAuthorName} from '../utils';
import HomeModalShell from './HomeModalShell';
import FeedPostReadonlyPreview from './FeedPostReadonlyPreview';
import styles from '../Home.module.css';

interface RepostPostModalProps {
  isOpen: boolean;
  targetPost: FeedPost | null;
  message: string;
  loading: boolean;
  error: string | null;
  validationError: string | null;
  textareaRef: RefObject<HTMLTextAreaElement>;
  failedImages: Record<string, boolean>;
  onImageError: (postId: string, mediaIndex: number) => void;
  onMessageChange: (value: string) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const RepostPostModal: React.FC<RepostPostModalProps> = ({
  isOpen,
  targetPost,
  message,
  loading,
  error,
  validationError,
  textareaRef,
  failedImages,
  onImageError,
  onMessageChange,
  onClose,
  onSubmit,
}) => {
  if (!isOpen || !targetPost) {
    return null;
  }

  const originalAuthorName = getAuthorName(targetPost);

  return (
    <HomeModalShell
      backdropClassName={styles.modalBackdrop}
      panelClassName={`${styles.modalCard} ${styles.repostModalCard}`}
      ariaLabelledBy="repost-post-modal-title"
      ariaDescribedBy="repost-post-modal-description"
      onClose={onClose}
    >
      <div className={styles.modalHeader}>
        <div>
          <h2 id="repost-post-modal-title" className={styles.modalTitle}>Repost Blog</h2>
          <p id="repost-post-modal-description" className={styles.modalDescription}>
            Add an optional message before reposting {originalAuthorName}&apos;s blog to your feed.
          </p>
        </div>
        <button
          type="button"
          className={styles.modalClose}
          onClick={onClose}
          aria-label="Close repost blog modal"
        >
          ×
        </button>
      </div>

      <form onSubmit={onSubmit} className={styles.modalForm}>
        <label htmlFor="repost-post-message" className={styles.modalLabel}>
          Your Message
          <span className={styles.modalLabelHint}>Optional</span>
        </label>
        <textarea
          id="repost-post-message"
          ref={textareaRef}
          value={message}
          onChange={(event) => onMessageChange(event.target.value)}
          className={`${styles.modalTextarea} ${styles.modalTextareaShort}`}
          placeholder="Add an optional message..."
          maxLength={5000}
          disabled={loading}
        />

        <div className={styles.repostComposerPreview}>
          <p className={styles.repostComposerPreviewTitle}>Original blog</p>
          <FeedPostReadonlyPreview
            post={targetPost}
            failedImages={failedImages}
            onImageError={onImageError}
            contextLabel={`Originally posted by ${originalAuthorName}`}
            unavailableTitle="Original blog unavailable"
            unavailableDescription="This blog is no longer available to preview before reposting."
          />
        </div>

        {validationError ? (
          <div className={`${styles.modalMessage} ${styles.modalValidationMessage}`}>
            {validationError}
          </div>
        ) : null}

        {error ? (
          <div className={`${styles.modalMessage} ${styles.modalErrorMessage}`}>
            {error}
          </div>
        ) : null}

        <div className={styles.modalActions}>
          <button
            type="button"
            className={`${styles.modalButton} ${styles.modalButtonSecondary}`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`${styles.modalButton} ${styles.modalButtonPrimary}`}
            disabled={loading}
          >
            {loading ? 'Reposting...' : 'Repost'}
          </button>
        </div>
      </form>
    </HomeModalShell>
  );
};

export default React.memo(RepostPostModal);
